import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Ellipsis } from '@/components/ui/ellipsis';
import { Skeleton } from '@/components/ui/skeleton';
import { AssigneesBadge } from '@/components/ui/assignees-badge';
import { useListStandardSourcesApi } from '@/api/regulatoryWatch';
import { getSourceIconUrl } from '@/utils/source-icon';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Gem,
  Search,
  Sparkles,
} from 'lucide-react';
import { PRIORITY_STANDARD_IDS } from '@ditto/shared';
import { useMemo, useRef, useState, type FC } from 'react';
import { SourceDescriptionPopover } from './SourceDescriptionPopover';
import { type SourceItem } from './SourceList';

const PAGE_SIZE = 20;

type StandardDisplayItem = {
  id: string;
  title: string;
  description: string | null;
  icon?: string | null;
  reasoning?: string | null;
  assignedMemberIds?: string[];
};

function sortStandards<T extends { id: string; title: string }>(
  items: T[],
  selectedIds: Set<string>
): T[] {
  return [...items].sort((a, b) => {
    const aSel = selectedIds.has(a.id) ? 0 : 1;
    const bSel = selectedIds.has(b.id) ? 0 : 1;
    if (aSel !== bSel) return aSel - bSel;
    const aPrio = PRIORITY_STANDARD_IDS.has(a.id) ? 0 : 1;
    const bPrio = PRIORITY_STANDARD_IDS.has(b.id) ? 0 : 1;
    if (aPrio !== bPrio) return aPrio - bPrio;
    return a.title.localeCompare(b.title);
  });
}

function filterBySearch<
  T extends { title: string; description?: string | null },
>(items: T[], query: string): T[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase();
  return items.filter(
    s =>
      s.title.toLowerCase().includes(q) ||
      s.description?.toLowerCase().includes(q)
  );
}

// ---- Server-fetched mode (Settings / SetupSubscribe) ----

export type StandardsSourceListProps = {
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onAssignSource?: (sourceId: string, memberIds: string[]) => void;
};

export const StandardsSourceList: FC<StandardsSourceListProps> = ({
  selectedIds,
  onToggle,
  onAssignSource,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { data, isPending } = useListStandardSourcesApi({
    page: 1,
    limit: 10000,
  });

  // Capture initial sort order once when data first loads
  const initialOrderRef = useRef<string[] | null>(null);
  const allItems: StandardDisplayItem[] = data?.data ?? [];
  if (allItems.length > 0 && !initialOrderRef.current) {
    initialOrderRef.current = sortStandards(allItems, selectedIds).map(
      i => i.id
    );
  }

  const items = useMemo(() => {
    const filtered = filterBySearch(allItems, searchQuery);
    if (initialOrderRef.current) {
      const orderMap = new Map(initialOrderRef.current.map((id, i) => [id, i]));
      return [...filtered].sort(
        (a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999)
      );
    }
    return sortStandards(filtered, selectedIds);
  }, [allItems, searchQuery, selectedIds]);

  if (isPending) return <StandardsListSkeleton />;

  return (
    <StandardsListCore
      items={items}
      selectedIds={selectedIds}
      onToggle={onToggle}
      onAssignSource={onAssignSource}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
    />
  );
};

// ---- Client-side mode (Onboarding with override sources) ----

export type StandardsSourceListOverrideProps = {
  sources: SourceItem[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
};

export const StandardsSourceListWithOverride: FC<
  StandardsSourceListOverrideProps
> = ({ sources, selectedIds, onToggle }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const items = useMemo(
    () => sortStandards(filterBySearch(sources, searchQuery), selectedIds),
    [sources, searchQuery, selectedIds]
  );

  return (
    <StandardsListCore
      items={items}
      selectedIds={selectedIds}
      onToggle={onToggle}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
    />
  );
};

// ---- Core list ----

const StandardsListCore: FC<{
  items: StandardDisplayItem[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onAssignSource?: (sourceId: string, memberIds: string[]) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
}> = ({
  items,
  selectedIds,
  onToggle,
  onAssignSource,
  searchQuery,
  onSearchChange,
}) => {
  const [page, setPage] = useState(0);

  // Reset page when search or items change
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = items.slice(
    safePage * PAGE_SIZE,
    (safePage + 1) * PAGE_SIZE
  );
  const selectedCount = items.filter(s => selectedIds.has(s.id)).length;

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
    setPage(0);
  };

  return (
    <>
      <div className="px-6 pt-4 pb-3 shrink-0 flex flex-col gap-3">
        <div className="relative w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500" />
          <Input
            placeholder="Search standards"
            value={searchQuery}
            onChange={e => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex items-center justify-between text-xs text-neutral-600">
          <div className="flex items-center gap-3">
            <span>
              {items.length === 0
                ? '0 standards'
                : `${safePage * PAGE_SIZE + 1}-${Math.min((safePage + 1) * PAGE_SIZE, items.length)} of ${items.length.toLocaleString()} standards`}
            </span>
            <span className="font-medium text-neutral-900">
              {selectedCount} selected
            </span>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <span className="mr-2">
                Page {safePage + 1} of {totalPages}
              </span>
              <PaginationButton
                onClick={() => setPage(0)}
                disabled={safePage <= 0}
              >
                <ChevronsLeft className="size-3.5" />
              </PaginationButton>
              <PaginationButton
                onClick={() => setPage(safePage - 1)}
                disabled={safePage <= 0}
              >
                <ChevronLeft className="size-3.5" />
              </PaginationButton>
              <PaginationButton
                onClick={() => setPage(safePage + 1)}
                disabled={safePage >= totalPages - 1}
              >
                <ChevronRight className="size-3.5" />
              </PaginationButton>
              <PaginationButton
                onClick={() => setPage(totalPages - 1)}
                disabled={safePage >= totalPages - 1}
              >
                <ChevronsRight className="size-3.5" />
              </PaginationButton>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 px-6 pb-4">
        <div className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
          {pageItems.map(source => (
            <StandardRow
              key={source.id}
              source={source}
              selected={selectedIds.has(source.id)}
              onToggle={() => onToggle(source.id)}
              onAssign={
                onAssignSource
                  ? memberIds => onAssignSource(source.id, memberIds)
                  : undefined
              }
            />
          ))}
          {pageItems.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No standards match the search.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const PaginationButton: FC<{
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex items-center justify-center size-7 rounded border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

// ---- Row ----

const StandardRow: FC<{
  source: StandardDisplayItem;
  selected: boolean;
  onToggle: () => void;
  onAssign?: (memberIds: string[]) => void;
}> = ({ source, selected, onToggle, onAssign }) => {
  const iconUrl = getSourceIconUrl(source.id, source.icon);

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-neutral-200 last:border-b-0">
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-black shrink-0">
            {source.title}
          </span>
          {source.description && (
            <>
              <SourceDescriptionPopover
                title={source.title}
                description={source.description}
              />
              <span className="text-sm text-neutral-600 truncate min-w-0">
                {source.description}
              </span>
            </>
          )}
        </div>
        {source.reasoning && (
          <div className="flex items-center gap-1.5 min-w-0">
            <Sparkles className="size-3 text-magenta-500 shrink-0" />
            <Ellipsis hasTooltip className="text-xs text-neutral-500">
              {source.reasoning}
            </Ellipsis>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {iconUrl && (
          <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-neutral-900 bg-[#fef3c7] border border-[#fde68a]">
            <img src={iconUrl} alt="" className="w-3.5 h-3.5 object-contain" />
            ISO
          </span>
        )}
        <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-neutral-900 bg-orange-100">
          <Gem className="size-3.5" />
          Standard
        </span>
        {selected && onAssign && (
          <div onClick={e => e.stopPropagation()}>
            <AssigneesBadge
              assignedMemberIds={source.assignedMemberIds ?? []}
              onAssign={onAssign}
            />
          </div>
        )}
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          className="shrink-0"
        />
      </div>
    </div>
  );
};

// ---- Skeleton ----

const StandardsListSkeleton: FC = () => (
  <div className="px-6 pt-4 pb-4 flex flex-col gap-3">
    <Skeleton className="h-10 w-[320px]" />
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      {Array.from({ length: 8 }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 px-4 py-3 border-b border-neutral-200 last:border-b-0"
        >
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <Skeleton className="h-4 w-[60%]" />
            <Skeleton className="h-3 w-[40%]" />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Skeleton className="h-6 w-12 rounded-lg" />
            <Skeleton className="h-6 w-[72px] rounded-lg" />
            <Skeleton className="h-4 w-4 rounded" />
          </div>
        </div>
      ))}
    </div>
  </div>
);
